import { useState } from "react";
import { Skeleton, Button, cn } from "@tellescope/ui";
import { Card } from "@tellescope/design-system";
import { formatDate, formatFileSize } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import { FileText, Image, File as FileIcon, Download, Loader2, Eye } from "lucide-react";
import type { File } from "@tellescope/types-models";
import type { FileListBlock as FileListBlockType } from "../../../shared/schema";
import { MobileList } from "../MobileList";
import { useEnduserFiles } from "../../hooks/useEnduserFiles";
import { useFileDownload } from "../../hooks/useFileDownload";
import { useFilePreview, isPreviewable } from "../../hooks/useFilePreview";
import { useFilesFilter } from "../../hooks/useItemFilter";
import { FilePreviewDialog } from "../FilePreviewDialog";

interface FileListBlockProps {
  block: FileListBlockType;
}

export function getFileIcon(mimeType: string | undefined) {
  if (mimeType?.startsWith("image/")) {
    return Image;
  }
  if (mimeType === "application/pdf" || mimeType?.includes("document")) {
    return FileText;
  }
  return FileIcon;
}

interface FileCardInternalProps {
  file: File;
  onDownload: (file: File) => Promise<void>;
  isDownloading: boolean;
  downloadingFileId: string | null;
  onPreview: (file: File) => Promise<void>;
  isLoadingPreview: boolean;
  previewingFileId: string | null;
}

function FileCardInternal({
  file,
  onDownload,
  isDownloading,
  downloadingFileId,
  onPreview,
  isLoadingPreview,
  previewingFileId,
}: FileCardInternalProps) {
  const { t } = useTranslation("files");
  const Icon = getFileIcon(file.type);
  const uploadDate = file.timestamp || file.updatedAt;
  const isThisFileDownloading = isDownloading && downloadingFileId === file.id;
  const isThisFileLoadingPreview = isLoadingPreview && previewingFileId === file.id;
  const canPreview = isPreviewable(file.type);

  return (
    <Card variant="floating" padding="root" className="overflow-hidden">
      <Card.Body className="px-4">
        <div className="flex gap-3 items-start">
          {/* File type icon */}
          <div className="flex-shrink-0">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon className="h-5 w-5 text-primary" />
            </div>
          </div>

          {/* File details */}
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate" title={file.name}>
              {file.name}
            </h4>

            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
              {formatFileSize(file.size) && (
                <>
                  <span>{formatFileSize(file.size)}</span>
                  <span>•</span>
                </>
              )}
              <span>{formatDate(uploadDate)}</span>
            </div>

            {file.tags && file.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {file.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground"
                  >
                    {tag}
                  </span>
                ))}
                {file.tags.length > 3 && <span className="text-xs text-muted-foreground">+{file.tags.length - 3}</span>}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex-shrink-0 flex gap-1">
            {/* Preview button - only shown for previewable files */}
            {canPreview && (
              <Button
                variant="ghost"
                size="sm"
                className={cn("h-8 w-8 p-0", isThisFileLoadingPreview && "animate-pulse")}
                onClick={() => onPreview(file)}
                disabled={isLoadingPreview || isDownloading}
                title={t("Preview")}
              >
                {isThisFileLoadingPreview ? <Loader2 className="h-4 w-4 animate-spin" /> : <Eye className="h-4 w-4" />}
              </Button>
            )}

            {/* Download button */}
            <Button
              variant="ghost"
              size="sm"
              className={cn("h-8 w-8 p-0", isThisFileDownloading && "animate-pulse")}
              onClick={() => onDownload(file)}
              disabled={isDownloading || isLoadingPreview}
              title={t("Download")}
            >
              {isThisFileDownloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

function FileListSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <Card variant="floating" padding="root" key={i}>
          <Card.Body className="px-4">
            <div className="flex gap-3">
              <Skeleton className="w-10 h-10 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
              <Skeleton className="w-8 h-8 rounded" />
            </div>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
}

export function FileListBlock({ block }: FileListBlockProps) {
  const { t } = useTranslation("files");
  const { itemFilter } = block.props;
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [previewingFileId, setPreviewingFileId] = useState<string | null>(null);

  const { files: rawFiles, isLoading: filesLoading, error } = useEnduserFiles();

  // Apply item filter if configured
  const { filteredItems: files, isLoading: filterLoading } = useFilesFilter({
    items: rawFiles,
    itemFilter,
  });

  const isLoading = filesLoading || filterLoading;
  const { download, isDownloading } = useFileDownload({
    onStart: () => {
      // Download started - state is managed via downloadingFileId
    },
    onError: (err) => {
      // Could show a toast notification here in the future
      console.error("File download failed:", err);
      setDownloadingFileId(null);
    },
  });

  const {
    loadPreview,
    previewUrl,
    previewFile,
    isLoading: isLoadingPreview,
    clearPreview,
  } = useFilePreview({
    onError: (err) => {
      console.error("File preview failed:", err);
      setPreviewingFileId(null);
    },
  });

  const handleDownload = async (file: File) => {
    setDownloadingFileId(file.id);
    try {
      await download(file);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const handlePreview = async (file: File) => {
    setPreviewingFileId(file.id);
    try {
      await loadPreview(file);
    } finally {
      setPreviewingFileId(null);
    }
  };

  const handleClosePreview = () => {
    clearPreview();
    setPreviewingFileId(null);
  };

  if (isLoading) {
    return <FileListSkeleton count={Math.min(block.props.maxItems ?? 10, 5)} />;
  }

  if (error) {
    return (
      <Card variant="floating" padding="root" className="border-destructive">
        <Card.Body className="p-4 text-center text-destructive">{t("Failed to load files")}</Card.Body>
      </Card>
    );
  }

  return (
    <>
      <MobileList
        items={files}
        renderItem={(file) => (
          <FileCardInternal
            file={file}
            onDownload={handleDownload}
            isDownloading={isDownloading}
            downloadingFileId={downloadingFileId}
            onPreview={handlePreview}
            isLoadingPreview={isLoadingPreview}
            previewingFileId={previewingFileId}
          />
        )}
        title={t("Your Files")}
        maxItems={block.props.maxItems ?? 10}
        emptyMessage={block.props.emptyMessage ?? t("No files available")}
      />

      <FilePreviewDialog
        file={previewFile}
        url={previewUrl}
        isLoading={isLoadingPreview}
        onClose={handleClosePreview}
      />
    </>
  );
}
